import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ShieldCheck,
  ArrowLeft,
  Lock,
  Upload,
  CheckCircle,
  XCircle,
  Calendar,
  FileText,
  Storefront,
  IdentificationCard,
} from "@phosphor-icons/react";
import { api, formatApiError, API } from "../lib/api";

/* ────────────────────────────────────────────────────────────────────
   /cc/mint/:code — public registration page reached by scanning the
   QR on a Curb Checkpoint Mint sticker.

   Flow:
     1. Page fetches /api/cc/mints/:code → if status != "unbound" we
        render a "this sticker is already used" lock screen.
     2. Otherwise show form: card code + activation password (security
        wall), doc type, doc name, business, doc date, reminder date,
        and a file picker.
     3. On submit, POST multipart/form-data to /api/cc/mints/:code/bind.
     4. On success render the bound confirmation card with a print
        button (sticker goes on the hard copy, customer walks out).
   ──────────────────────────────────────────────────────────────────── */

const DOC_TYPES = [
  { value: "passport", label: "Passport" },
  { value: "id", label: "ID" },
  { value: "drivers_licence", label: "Driver's Licence" },
  { value: "pdp", label: "PDP" },
  { value: "police_clearance", label: "Police Clearance" },
  { value: "graduation_certificate", label: "Graduation Certificate" },
  { value: "other", label: "Other Document" },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function plusDaysISO(d) {
  const dt = new Date();
  dt.setDate(dt.getDate() + d);
  return dt.toISOString().slice(0, 10);
}

export default function CheckpointMintBind() {
  const { code } = useParams();
  const mintCode = (code || "").toUpperCase();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [statusErr, setStatusErr] = useState("");

  // Form state
  const [cardCode, setCardCode] = useState("");
  const [cardPassword, setCardPassword] = useState("");
  const [docType, setDocType] = useState("pdp");
  const [docName, setDocName] = useState("");
  const [business, setBusiness] = useState("");
  const [docDate, setDocDate] = useState(todayISO());
  const [reminderDate, setReminderDate] = useState(plusDaysISO(90));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bindErr, setBindErr] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/cc/mints/${mintCode}`);
        if (!cancel) setStatus(r.data);
      } catch (e) {
        if (!cancel) setStatusErr(formatApiError(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [mintCode]);

  async function submit(e) {
    e.preventDefault();
    if (!file) {
      setBindErr("Please attach the document file");
      return;
    }
    if (!cardCode || !cardPassword) {
      setBindErr("Card code and password are required");
      return;
    }
    setBusy(true);
    setBindErr("");
    try {
      const fd = new FormData();
      fd.append("card_code", cardCode);
      fd.append("card_password", cardPassword);
      fd.append("doc_type", docType);
      fd.append("doc_name", docName);
      fd.append("business", business);
      fd.append("doc_date", docDate);
      fd.append("reminder_date", reminderDate);
      fd.append("document", file);
      const r = await api.post(`/cc/mints/${mintCode}/bind`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
    } catch (e) {
      setBindErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-6">
        <div className="font-mono text-xs tracking-widest text-neutral-500">
          Loading mint…
        </div>
      </div>
    );
  }

  if (statusErr || !status) {
    return (
      <LockScreen
        title="Sticker not found"
        body={statusErr || "This Curb Checkpoint sticker is not registered. Check that you scanned the right QR or contact the issuer."}
      />
    );
  }

  if (status.status !== "unbound") {
    return (
      <VerifiedBadge
        status={status}
        mintCode={mintCode}
      />
    );
  }

  // Success view
  if (result) {
    return <BindSuccess mintCode={mintCode} result={result} business={business} />;
  }

  // Active binding form
  return (
    <div className="min-h-screen bg-neutral-100 text-neutral-900">
      {/* Header */}
      <header className="bg-black text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/cc/enterprise"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/70 hover:text-white"
            data-testid="mint-back"
          >
            <ArrowLeft size={14} weight="bold" /> Curb Checkpoint
          </Link>
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase">
            <ShieldCheck size={14} weight="bold" /> MINT · BIND
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="font-mono text-[11px] tracking-[0.4em] uppercase text-neutral-500">
          CURB CHECKPOINT
        </div>
        <h1 className="font-display text-3xl sm:text-5xl leading-tight mt-3">
          Bind this document<br /> to your card.
        </h1>
        <p className="mt-4 text-sm text-neutral-700 leading-relaxed max-w-xl">
          You&apos;re about to lock the document below to your existing Curb
          Crew card. Once bound this mint sticker can never be reused.
          The document is encrypted and only you can open it.
        </p>

        <div className="mt-6 inline-flex items-center gap-2 font-mono text-xs bg-black text-white px-3 py-1.5">
          <Lock size={12} weight="bold" /> {mintCode}
        </div>

        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="mint-form">
          {/* SECURITY WALL */}
          <section className="bg-white border-2 border-black p-6">
            <div className="flex items-center gap-2">
              <Lock size={14} weight="bold" style={{ color: "#E30613" }} />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase">
                Security wall · card auth
              </span>
            </div>
            <p className="text-xs text-neutral-600 mt-2">
              Enter your existing Curb Crew card code and the password you
              set when you activated it. We use this to confirm the card
              is yours before binding.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Field
                label="Card code"
                hint="e.g. 2RL6R7QU"
                icon={IdentificationCard}
                value={cardCode}
                onChange={(v) => setCardCode(v.toUpperCase())}
                testid="mint-card-code"
                required
              />
              <Field
                label="Activation password"
                type="password"
                icon={Lock}
                value={cardPassword}
                onChange={setCardPassword}
                testid="mint-card-password"
                required
              />
            </div>
          </section>

          {/* DOCUMENT DETAILS */}
          <section className="bg-white border-2 border-black p-6">
            <div className="flex items-center gap-2">
              <FileText size={14} weight="bold" style={{ color: "#E30613" }} />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase">
                Document details
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <SelectField
                label="Document type"
                value={docType}
                onChange={setDocType}
                options={DOC_TYPES}
                testid="mint-doc-type"
              />
              <Field
                label="Name on document"
                hint={
                  docType === "other"
                    ? "What is this? e.g. Roadworthy, Rates statement"
                    : "As printed on the document"
                }
                value={docName}
                onChange={setDocName}
                testid="mint-doc-name"
                required
              />
              <Field
                label="Business / issuer"
                hint="e.g. Postnet Strand"
                icon={Storefront}
                value={business}
                onChange={setBusiness}
                testid="mint-business"
                required
              />
              <Field
                label="Issue date"
                type="date"
                value={docDate}
                onChange={setDocDate}
                testid="mint-doc-date"
                required
              />
              <Field
                label="Remind me on"
                type="date"
                icon={Calendar}
                value={reminderDate}
                onChange={setReminderDate}
                testid="mint-reminder-date"
                hint="Email reminder when the document goes stale"
                required
              />
            </div>
          </section>

          {/* FILE UPLOAD */}
          <section className="bg-white border-2 border-black p-6">
            <div className="flex items-center gap-2">
              <Upload size={14} weight="bold" style={{ color: "#E30613" }} />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase">
                Upload document
              </span>
            </div>
            <p className="text-xs text-neutral-600 mt-2">
              Image of the document (JPG / PNG / HEIC). We compress it to
              ≤250KB before storing. SHA-256 hash of the original is kept
              for tamper-detection.
            </p>
            <label
              htmlFor="mint-file"
              className="mt-4 block border-2 border-dashed border-neutral-400 p-6 text-center cursor-pointer hover:border-black"
              data-testid="mint-file-dropzone"
            >
              <input
                id="mint-file"
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="mint-file-input"
              />
              {file ? (
                <div className="font-mono text-xs">
                  <strong>{file.name}</strong>
                  <div className="text-neutral-500 mt-1">
                    {(file.size / 1024).toFixed(0)} KB · ready to bind
                  </div>
                </div>
              ) : (
                <div className="font-mono text-xs text-neutral-500">
                  Tap to pick a file
                </div>
              )}
            </label>
          </section>

          {bindErr && (
            <div className="bg-red-50 border-2 border-red-600 text-red-800 p-4 text-sm flex items-start gap-2" data-testid="mint-error">
              <XCircle size={16} weight="bold" className="shrink-0 mt-0.5" />
              <span>{bindErr}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-black text-white font-mono text-[11px] tracking-[0.3em] uppercase py-4 disabled:opacity-40"
            data-testid="mint-submit"
          >
            {busy ? "Binding…" : "Bind document to my card"}
          </button>

          <p className="text-xs text-neutral-500 text-center">
            By submitting you confirm this is your own Curb Crew card and
            you consent to the document being stored encrypted under your
            vault. You can delete it later from your card dashboard.
          </p>
        </form>
      </main>
    </div>
  );
}

/* ─── Reusable bits ─── */

function Field({ label, hint, type = "text", value, onChange, icon: Icon, testid, required }) {
  return (
    <label className="block">
      <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">
        {label}
      </div>
      <div className="mt-1 flex items-center gap-2 border-b-2 border-neutral-300 focus-within:border-black">
        {Icon ? <Icon size={14} weight="bold" className="text-neutral-400" /> : null}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className="flex-1 bg-transparent py-2 outline-none text-sm"
          data-testid={testid}
        />
      </div>
      {hint && <div className="text-[11px] text-neutral-400 mt-1">{hint}</div>}
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">
        {label}
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border-b-2 border-neutral-300 bg-transparent py-2 outline-none text-sm focus:border-black"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function LockScreen({ title, body }) {
  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border-2 border-black p-8">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-500">
          <Lock size={14} weight="bold" /> Curb Checkpoint
        </div>
        <h1 className="font-display text-3xl mt-3 leading-tight">{title}</h1>
        <p className="mt-3 text-sm text-neutral-700 leading-relaxed">{body}</p>
        <Link
          to="/cc/enterprise"
          className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase border-2 border-black px-4 py-2"
          data-testid="mint-lock-back"
        >
          <ArrowLeft size={12} weight="bold" /> Back
        </Link>
      </div>
    </div>
  );
}

/* ─── Verified badge — what a third-party verifier sees when they scan
   the sticker on a printed certified copy. Public, no PII, just a
   provenance receipt. ─── */
function VerifiedBadge({ status, mintCode }) {
  const isRetired = status.status === "deleted";

  // Freshness chip — green if still in window, red if stale.
  let freshLabel = null;
  let freshColor = "#15803d";
  if (status.reminder_date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(status.reminder_date + "T00:00:00");
    const days = Math.floor((expiry - today) / 86400000);
    if (days < 0) {
      freshLabel = `STALE · ${Math.abs(days)}d past refresh`;
      freshColor = "#dc2626";
    } else {
      freshLabel = `Fresh · ${days}d until refresh`;
    }
  }

  const accent = isRetired ? "#dc2626" : "#15803d";
  const headline = isRetired ? "RETIRED" : "VERIFIED";
  const blurb = isRetired
    ? "This sticker has been deleted by the cardholder and is permanently retired. The document is no longer trusted."
    : "This Curb Checkpoint sticker is genuine and bound to a cardholder's vault. The metadata below is published by Curb Crew.";

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border-2 border-black" data-testid="mint-verified-badge">
        <div
          className="px-6 py-5 text-white"
          style={{ background: accent }}
        >
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase">
            {isRetired ? <XCircle size={14} weight="bold" /> : <CheckCircle size={14} weight="bold" />}
            CURB CHECKPOINT · {headline}
          </div>
          <h1 className="font-display text-3xl mt-2 leading-tight">
            {status.doc_type_label || "Bound document"}
          </h1>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-neutral-700 leading-relaxed">{blurb}</p>
          <div className="border-t border-neutral-200 pt-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-400">ISSUER</div>
              <div className="font-bold mt-0.5">{status.business || "—"}</div>
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-400">BOUND</div>
              <div className="font-bold mt-0.5">{(status.bound_at || "").slice(0, 10) || "—"}</div>
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-400">DOC DATE</div>
              <div className="font-bold mt-0.5">{status.doc_date || "—"}</div>
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-400">REFRESH</div>
              <div className="font-bold mt-0.5">{status.reminder_date || "—"}</div>
            </div>
          </div>
          {freshLabel && !isRetired && (
            <div
              className="font-mono text-[10px] tracking-[0.3em] uppercase px-3 py-2 inline-flex items-center gap-2"
              style={{ background: freshColor, color: "white" }}
              data-testid="mint-verified-freshness"
            >
              {freshLabel}
            </div>
          )}
          <div className="border-t border-neutral-200 pt-4">
            <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-400">SECURITY</div>
            <p className="text-xs text-neutral-600 mt-1 leading-relaxed">
              The cardholder&apos;s password is required to open the document
              itself. This page proves that the sticker is real and bound —
              not the contents.
            </p>
            <div className="font-mono text-[10px] text-neutral-400 mt-2 break-all">
              {mintCode}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BindSuccess({ mintCode, result, business }) {
  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-6">
      <div className="max-w-lg w-full bg-white border-2 border-black p-8" data-testid="mint-success">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-green-700">
          <CheckCircle size={14} weight="bold" /> BOUND
        </div>
        <h1 className="font-display text-4xl mt-3 leading-tight">
          {result.doc_type_label}<br />locked to your card.
        </h1>
        <ul className="mt-6 space-y-2 text-sm">
          <li>
            <span className="font-mono text-[10px] tracking-widest uppercase text-neutral-400 mr-2">
              MINT
            </span>
            <code className="font-mono">{mintCode}</code>
          </li>
          <li>
            <span className="font-mono text-[10px] tracking-widest uppercase text-neutral-400 mr-2">
              ISSUER
            </span>
            {business || "—"}
          </li>
          <li>
            <span className="font-mono text-[10px] tracking-widest uppercase text-neutral-400 mr-2">
              SIZE
            </span>
            {result.stored_kb} KB
          </li>
          <li>
            <span className="font-mono text-[10px] tracking-widest uppercase text-neutral-400 mr-2">
              HASH
            </span>
            <code className="font-mono text-[10px] break-all">{result.sha256?.slice(0, 24)}…</code>
          </li>
        </ul>
        <p className="mt-6 text-sm text-neutral-700 leading-relaxed">
          The sticker can now be stuck onto the certified hard copy and
          handed to the customer. They&apos;ll see this document under the
          <strong> Checkpoint</strong> tab when they sign in to{" "}
          <Link to="/my-cards" className="underline">my-cards</Link>.
        </p>
        <div className="mt-8 grid grid-cols-2 gap-3">
          <Link
            to="/cc/enterprise"
            className="text-center border-2 border-black px-4 py-3 font-mono text-[10px] tracking-[0.3em] uppercase"
            data-testid="mint-success-done"
          >
            Done
          </Link>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.print();
            }}
            className="text-center bg-black text-white px-4 py-3 font-mono text-[10px] tracking-[0.3em] uppercase"
            data-testid="mint-success-print"
          >
            Print receipt
          </a>
        </div>
      </div>
    </div>
  );
}
